import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import {generateGuid} from "../../util/guid";
import Tooltip from "../common/Tooltip";
import * as types from "../../constants/ComponentConstants";
import ValidationMessage from "../common/ValidationMessage";
import Revert from "../common/Revert";
import { Popup } from 'semantic-ui-react';
import mergeDeep from "../../util/mergeDeep";
import { insert } from "../../util/stringExtensions";

const TextInput = (props) => {
	const hasInputMask = !!props.inputmask && (!props.ignoreInputMaskIfStartsWith.length || !props.ignoreInputMaskIfStartsWith.filter(x => props.value !== null && props.value.startsWith(x)).length);
	const hasValue = !!props.value || hasInputMask;
	const hasValidationMessage = !!props.validationMessage;
	const validationMessageType = props.validationMessageType;
	const tooltiptype = props.tooltipType || types.TOOLTIP_INFO;
	let value = props.value ? props.value : "";
	const sizeClassName = props.size ? "o-col-" + props.size : "o-col-12";
	const label = props.label + (props.optional ? " (Optioneel)" : "");
	const validation = hasValidationMessage ? <ValidationMessage message={props.validationMessage} type={validationMessageType} /> : null;
	const maxLength = props.maxLength ? props.maxLength : "";
	const disabled = props.disabled || props.readOnly;
	const id = props.id ? props.id : generateGuid();
	let style = mergeDeep({}, props.style);
	const faIconClassname = props.faIconClassname;
	const isOndernemingsnummer = props.isOndernemingsnummer;

	const surroundingStyle = props.surroundingStyle;

	const differsFromOriginal = props.originalValue !== undefined ? (props.originalValue || "") !== (props.value || "") : false;
	const revertable = (props.disabled !== true && props.disabled !== 'true') && differsFromOriginal && !props.hideRevert;

	let info = props.tooltip ?
		<Tooltip isInput text={props.tooltip} tooltipType={tooltiptype} tooltipColor={props.tooltipColor} /> :
		((props.markChange && differsFromOriginal) ? <Tooltip isInput text={`Originele waarde: ${props.originalValue ? props.originalValue : 'leeg'}`} tooltipType={tooltiptype} /> : null);

	const inputClassName = classnames({
		"c-input__field": true,
		"yellowBackground": props.markChange && differsFromOriginal
	});

	const labelClassName = classnames({
		"c-input__label": true,
		"has-value": hasValue
	});

	const containerClassName = classnames({
		"c-input": true,
		"has-value": hasValue,
		"error": hasValidationMessage,
		"warning": hasValidationMessage && validationMessageType === types.ValidationMessageTypes.WARNING,
		"disabled": disabled
	});

	function handleOnKeyUp(event) {
		if (event.which === 13) return props.onEnter ? props.onEnter() : null;
	}

	function handleChange(event) {
		const hasInputMaskOnChange = !!props.inputmask && (!props.ignoreInputMaskIfStartsWith.length || !props.ignoreInputMaskIfStartsWith.filter(x => props.value !== null && event.target.value.startsWith(x)).length);

		if (isOndernemingsnummer)
			event.target.value = event.target.value.replace('.', '').replace('BE', '').replace(' ', '');
		else if (hasInputMaskOnChange && props.inputmask.length > (event.target.value ? event.target.value.length : 0)) {
			event.target.value = props.inputmask;
		} else if (hasInputMaskOnChange && event.target.value.indexOf(props.inputmask) === -1)
			event.target.value = props.inputmask + event.target.value;

		props.onChange(event);
	}

	if (hasInputMask && value.indexOf(props.inputmask) === -1) {
		value = props.inputmask + value;

		const event = {
			target: {
				value: value,
				name: props.name
			}
		};

		props.onChange(event);
	}

	if (isOndernemingsnummer) {
		value = value.replace('.', '').replace('BE', '').replace(' ', '');

		if (value.length == 10)
			value = `BE ${insert(insert(value, 7, '.'), 4, '.')}`;

		//BE 0248.142.826
	}

	const hasTooltip = props.tooltip || (props.markChange && differsFromOriginal);

	const faIconStyle = hasTooltip && revertable ? "c-socialicon_shifted_double_left" : ((hasTooltip || revertable) ? "c-socialicon_shifted_left" : "c-socialicon");

	return (
		<div className={sizeClassName}>
			<div className={containerClassName} style={surroundingStyle}>
				<input
					id={id}
					style={style}
					value={value}
					disabled={disabled}
					onChange={handleChange}
					className={inputClassName}
					maxLength={maxLength}
					name={props.name}
					onKeyUp={handleOnKeyUp}
					ref={props.hasFocus ? input => input && input.focus() : null} />
				{props.label ?
					<label
						className={labelClassName}
						htmlFor={id}>
						{label}
					</label> : null}
				{info}
				{(!faIconClassname || !value) || <Popup trigger={<a className={classnames({ [faIconStyle]: true })} href={value} target="_blank">
					<i className={"fa fa-fw " + faIconClassname}></i>
				</a>} content={`Test hier je link: ${value}`} />}
				{!revertable || <Revert
					onChange={handleChange}
					name={props.name}
					originalValue={props.originalValue} />}
			</div>
			{validation}
		</div>
	);
};

TextInput.defaultProps = {
	validationMessageType: "error",
	ignoreInputMaskIfStartsWith: [],
	markChange: false,
	style: {}
};

TextInput.propTypes = {
	name: PropTypes.string.isRequired,
	id: PropTypes.string,
	label: PropTypes.string.isRequired,
	value: PropTypes.string,
	originalValue: PropTypes.string,
	markChange: PropTypes.bool,
	onChange: PropTypes.func,
	maxLength: PropTypes.number,
	tooltip: PropTypes.string,
	tooltipType: PropTypes.string,
	tooltipColor: PropTypes.string,
	validationMessage: PropTypes.string,
	validationMessageType: PropTypes.string,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	size: PropTypes.number,
	optional: PropTypes.bool,
	style: PropTypes.object,
	surroundingStyle: PropTypes.object,
	inputmask: PropTypes.string,
	ignoreInputMaskIfStartsWith: PropTypes.arrayOf(PropTypes.string),
	hideRevert: PropTypes.bool,
	faIconClassname: PropTypes.string,
	onEnter: PropTypes.func,
	isOndernemingsnummer: PropTypes.bool,
	hasFocus: PropTypes.bool
};

export default TextInput;